import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  IFormbuilderFieldTypeConfiguration,
  ICustomIdInputFieldSettingsModel,
} from '../../../models/form-builder-types-configurations.model';
import {
  baseFieldUpdateFromSettingsFormModel,
  getDefaultBaseSettingsConfigFormly,
  getPopulatesPropsSettingsConfigFormly,
  populatesPropsParseIntoSettingsFormModel,
  populatesPropsUpdateFromSettingsFormModel,
} from '../form-fields-formly-settings/form-builder-default-settings-configuration-formly-fields.utils';
import {
  parseRequiredValidatorParseFieldIntoModel,
  parseInvalidAppNoValidatorParseFieldIntoModel,
} from '../form-fields-formly-validations/form-fields-formly-validation-field-parse-to-model.util';
import {
  setRequiredValidatorUpdateFieldFromModel,
  setInvalidAppNoFormDataIntoField,
} from '../form-fields-formly-validations/form-fields-formly-validation-field-set-from-model.util';
import { FormBuilderService } from '../../../services/form-builder.service';
import { IFormFieldsDisplayModel } from '../../../models/form-fields-formly-display.model';
import { parseFieldIntoDisplayFormModel } from '../form-fields-formly-display/form-fields-formly-display-field-parse-to-model.util';
import { setDisplayHideExpressionFromModel } from '../form-fields-formly-display/form-fields-formly-display-field-set-from-model.util';
import { fieldsBaseConfigurationFormModel } from './shared.configuration';
import {
  getRequiredValidatorFormConfig,
  getValidationFieldsFormConfig,
} from '../form-fields-formly-validations/form-fields-formly-validation-field-generator.util';
import { getFieldDisplayFormConfig } from '../form-fields-formly-display/form-fields-formly-display-field-generator.util';
import { EIremboFormlyFieldTypes } from '@irembo-andela/irembogov3-common';
import { IFormFieldsValidationModel } from 'src/app/core/models/form-fields-formly-validation.model';

export const applicationNumberResponseProps: Set<string> = new Set([
  'applicationStatus',
  'Surnameg6k6N',
  'serviceId',
  'applicationNumber',
]);

interface IApplicationNumberFieldContentConfig {
  url?: string;
  useBaseUrl?: boolean;
  appNoLength?: number;
  compareKey?: string;
  compareOperator?: string;
  compareValue?: string;
  mixpanelEventAnalytics?: boolean;
}

interface IApplicationNumberConfigurationFormModel
  extends ICustomIdInputFieldSettingsModel {
  applicationNumberFieldContentConfig?: IApplicationNumberFieldContentConfig;
}

export const applicationNumberGetConfigurationConfig = (
  formBuilderServiceInstance: FormBuilderService
): IFormbuilderFieldTypeConfiguration => {
  return {
    settings: {
      formModel: {
        ...fieldsBaseConfigurationFormModel(),
        applicationNumberFieldContentConfig: {
          url: '/application/v1/application/by-application-number/details',
          useBaseUrl: true,
          appNoLength: 19,
          compareKey: 'serviceId',
          compareOperator: '!==',
          compareValue: '',
        },
      },
      formlyForm: [
        ...getApplicationNumberFieldSettingsConfig(),
        ...getDefaultBaseSettingsConfigFormly(formBuilderServiceInstance),
        ...getPopulatesPropsSettingsConfigFormly(formBuilderServiceInstance, [
          ...Array.from(applicationNumberResponseProps),
        ]),
      ],
    },
    validations: {
      formModel: {},
      formlyForm: [
        getRequiredValidatorFormConfig(),
        getValidationFieldsFormConfig(formBuilderServiceInstance),
      ],
    },
    display: {
      formModel: {},
      formlyForm: [...getFieldDisplayFormConfig(formBuilderServiceInstance)],
    },
  };
};

export const applicationNumberFieldParseIntoSettingsFormModel = (
  field: FormlyFieldConfig
): IApplicationNumberConfigurationFormModel => {
  const model: IApplicationNumberConfigurationFormModel =
    populatesPropsParseIntoSettingsFormModel(field);

  model.applicationNumberFieldContentConfig = {
    url: field.props?.['url'],
    useBaseUrl: field.props?.['useBaseUrl'],
    appNoLength: field.props?.['appNoLength'],
    compareKey: field.props?.['compareKey'],
    compareOperator: field.props?.['compareOperator'],
    compareValue: field.props?.['compareValue'],
    mixpanelEventAnalytics: field.props?.['mixpanelEventAnalytics'],
  };

  return model;
};

export const applicationNumberFieldUpdateFromSettingsFormModel = (
  model: IApplicationNumberConfigurationFormModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  field = baseFieldUpdateFromSettingsFormModel(model, field);
  field = populatesPropsUpdateFromSettingsFormModel(model, field);

  if (field.props) {
    field.props['url'] = model.applicationNumberFieldContentConfig?.url;
    field.props['useBaseUrl'] =
      model.applicationNumberFieldContentConfig?.useBaseUrl;
    field.props['appNoLength'] =
      model.applicationNumberFieldContentConfig?.appNoLength;
    field.props['compareKey'] =
      model.applicationNumberFieldContentConfig?.compareKey;
    field.props['compareOperator'] =
      model.applicationNumberFieldContentConfig?.compareOperator;
    field.props['compareValue'] =
      model.applicationNumberFieldContentConfig?.compareValue;
    field.props['mixpanelEventAnalytics'] =
      model.applicationNumberFieldContentConfig?.mixpanelEventAnalytics;
  } else {
    field.props = {
      url: model.applicationNumberFieldContentConfig?.url,
      useBaseUrl: model.applicationNumberFieldContentConfig?.useBaseUrl,
      appNoLength: model.applicationNumberFieldContentConfig?.appNoLength,
      compareKey: model.applicationNumberFieldContentConfig?.compareKey,
      compareOperator:
        model.applicationNumberFieldContentConfig?.compareOperator,
      compareValue: model.applicationNumberFieldContentConfig?.compareValue,
      mixpanelEventAnalytics:
        model.applicationNumberFieldContentConfig?.mixpanelEventAnalytics,
    };
  }

  return field;
};

export const applicationNumberFieldParseIntoValidationFormModel = (
  field: FormlyFieldConfig
): IFormFieldsValidationModel => {
  let model: IFormFieldsValidationModel = {};

  model = parseRequiredValidatorParseFieldIntoModel(model, field);
  model = parseInvalidAppNoValidatorParseFieldIntoModel(model, field);

  return model;
};

export const applicationNumberFieldUpdateFromValidationFormModel = (
  model: IFormFieldsValidationModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  field = setRequiredValidatorUpdateFieldFromModel(model, field);
  field = setInvalidAppNoFormDataIntoField(model, field);

  return field;
};

export const applicationNumberFieldParseFieldIntoDisplayFormModel = (
  field: FormlyFieldConfig
): IFormFieldsDisplayModel => {
  return parseFieldIntoDisplayFormModel(field);
};

export const applicationNumberFieldUpdateFromDisplayFormModel = (
  model: IFormFieldsDisplayModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  return setDisplayHideExpressionFromModel(model, field);
};

export const getApplicationNumberFieldSettingsConfig =
  (): FormlyFieldConfig[] => {
    return [
      {
        key: 'applicationNumberFieldContentConfig',
        id: 'applicationNumberFieldContentConfig',
        type: EIremboFormlyFieldTypes['formly-group'],
        props: {
          dragDisabled: false,
        },
        fieldGroup: [
          {
            key: 'url',
            type: 'input',
            props: {
              label: 'API URL',
              required: true,
              placeholder: 'Enter API URL',
            },
          },
          {
            key: 'appNoLength',
            type: 'input',
            props: {
              type: 'number',
              label: 'Application Number Length',
              required: true,
              placeholder: 'Enter Length',
            },
          },
          {
            key: 'compareKey',
            type: 'input',
            props: {
              label: 'Compare Key',
              required: true,
              placeholder: 'Enter Compare Key',
            },
          },
          {
            key: 'compareOperator',
            type: 'select',
            props: {
              label: 'Compare Operator',
              required: true,
              options: [
                { label: 'Equals', value: '===' },
                { label: 'Not Equals', value: '!==' },
              ],
            },
          },
          {
            key: 'compareValue',
            type: 'input',
            props: {
              label: 'Compare Value',
              required: true,
              placeholder: 'Enter Compare Value',
            },
          },
          {
            key: 'useBaseUrl',
            type: 'checkbox',
            props: {
              label: 'Use Base URL',
            },
          },
          {
            key: 'mixpanelEventAnalytics',
            type: 'checkbox',
            defaultValue: false,
            props: {
              label: 'Enable MixPanel Analytics',
              formCheck: 'switch',
            },
          },
        ],
      },
    ];
  };
